<template>
  <div class="article-detail-page">
    <!-- 页面顶部横幅 -->
    <div class="page-banner">
      <div class="container banner-inner">
        <h1 class="banner-title">{{ pageTitle }}</h1>
        <el-breadcrumb separator="/" class="banner-breadcrumb">
          <el-breadcrumb-item :to="{ path: '/' }">{{ $t('m.home') }}</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: listPath, query: { pname: pageTitle, pid: categoryId } }">{{ pageTitle }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ $t('m.detail') }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="container page-content" v-if="!loading">
      <div class="detail-wrapper" v-if="info">
        <!-- 文章头部 -->
        <div class="detail-header">
          <h1 class="detail-title">{{ info.title_name }}</h1>
          <div class="detail-meta" v-if="showMeta">
            <span v-if="info.create_time"><i class="el-icon-time"></i> {{ formatTime(info.create_time) }}</span>
            <span v-if="info.author"><i class="el-icon-user"></i> {{ info.author }}</span>
            <span><i class="el-icon-view"></i> {{ $t('m.views') }} {{ info.views || 0 }}</span>
          </div>
        </div>

        <!-- 封面图（图文模式显示） -->
        <div class="detail-cover" v-if="showCover && info.url_image">
          <img :src="fixUrl(info.url_image)" :alt="info.title_name" class="cover-img">
        </div>

        <!-- 正文内容：渲染前先做安全清洗，后台内容不可信 -->
        <div class="detail-body" v-html="content" v-if="content"></div>
        <div class="empty-content" v-else>
          <i class="el-icon-document-delete"></i>
          <p>{{ $t('m.noContent') }}</p>
        </div>

        <!-- 上一篇/下一篇 -->
        <div class="detail-nav" v-if="front || after">
          <div class="nav-item prev" @click="toPre" v-if="front">
            <span class="nav-label"><i class="el-icon-arrow-left"></i> {{ $t('m.prev') }}</span>
            <span class="nav-title text-ellipsis">{{ front.title_name }}</span>
          </div>
          <div class="nav-item next" @click="toNext" v-if="after">
            <span class="nav-label">{{ $t('m.next') }} <i class="el-icon-arrow-right"></i></span>
            <span class="nav-title text-ellipsis">{{ after.title_name }}</span>
          </div>
        </div>
      </div>

      <!-- 请求失败/无数据 -->
      <div class="empty-state" v-else>
        <i class="el-icon-document-delete"></i>
        <p>{{ $t('m.noContent') }}</p>
      </div>
    </div>

    <!-- 加载中 -->
    <div class="loading-state" v-else>
      <i class="el-icon-loading"></i>
      <p>{{ $t('m.loading') }}</p>
    </div>
  </div>
</template>

<script>
import url from "../request/base";
import { fixUrl as fixImgUrl, formatRichText } from "../utils/site";

export default {
  name: 'ArticleDetail',
  props: {
    // 内容ID
    articleId: {
      type: [String, Number],
      default: 0
    },
    // 页面标题（分类名称）
    pageTitle: {
      type: String,
      default: '详情'
    },
    // 分类ID
    categoryId: {
      type: [String, Number],
      default: 0
    },
    // 列表页路径（用于面包屑和返回）
    listPath: {
      type: String,
      default: '/latestbroadcast'
    },
    // 详情页路径（用于上一篇下一篇跳转）
    detailPath: {
      type: String,
      default: '/latestbroadcast/latestdetail'
    },
    // 是否显示封面图（整站默认隐藏：详情卡只展示正文，封面不显示）
    showCover: {
      type: Boolean,
      default: false
    },
    // 是否显示元信息（日期、作者、阅读量）
    showMeta: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      imgurl: url.baseUrlimg,
      info: null,
      front: null,
      after: null,
      loading: false
    }
  },
  watch: {
    articleId: {
      handler() {
        this.getInfo()
      }
    }
  },
  created() {
    this.getInfo()
  },
  computed: {
    // 正文：后台返回的富文本 textcontent，先清洗并让图片自适应后再 v-html 渲染
    content() {
      return formatRichText(this.info && this.info.textcontent ? this.info.textcontent : '')
    }
  },
  methods: {
    // 拼接图片地址：后端若已返回完整 URL 则不再拼前缀
    fixUrl(path) {
      return fixImgUrl(this.imgurl, path)
    },
    // 格式化时间戳
    formatTime(timestamp) {
      if (!timestamp) return ''
      if (String(timestamp).length === 10) {
        timestamp = timestamp * 1000
      }
      const date = new Date(Number(timestamp))
      if (isNaN(date.getTime())) return timestamp
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    getInfo() {
      this.loading = true
      this.$api.home.getDetail({
        id: this.articleId
      }).then(res => {
        if (res.data) {
          this.info = res.data.datails
          this.after = res.data.after
          this.front = res.data.front
        }
      }).catch(err => {
        console.error('ArticleDetail getInfo error:', err)
      }).finally(() => {
        this.loading = false
      })
    },
    toPre() {
      if (this.front) {
        this.$router.push({
          path: this.detailPath,
          query: {
            id: this.front.id,
            name: this.front.title_name,
            pname: this.pageTitle,
            pid: this.categoryId
          }
        })
      }
    },
    toNext() {
      if (this.after) {
        this.$router.push({
          path: this.detailPath,
          query: {
            id: this.after.id,
            name: this.after.title_name,
            pname: this.pageTitle,
            pid: this.categoryId
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.article-detail-page {
  min-width: 1200px;
  background-color: var(--color-bg-light);
}

/* ========== 页面横幅 ========== */
.page-banner {
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
  padding: 50px 0;
  position: relative;
  overflow: hidden;
}

.page-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.banner-inner {
  position: relative;
  z-index: 1;
}

.banner-title {
  font-size: 36px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-inverse);
  margin: 0 0 16px 0;
  letter-spacing: 4px;
}

.banner-breadcrumb {
  font-size: var(--font-size-sm);
}

.banner-breadcrumb :deep(.el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.7);
}

.banner-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-medium);
}

.banner-breadcrumb :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.5);
}

/* ========== 主体内容 ========== */
.page-content {
  padding: 50px 0;
  min-height: 500px;
}

.detail-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  background-color: var(--color-bg-white);
  border-radius: var(--radius-lg);
  padding: 50px 60px;
  box-shadow: var(--shadow-sm);
}

/* 文章头部 */
.detail-header {
  text-align: center;
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--color-border-light);
}

.detail-title {
  font-size: 28px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 20px 0;
  line-height: var(--line-height-tight);
}

.detail-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  font-size: var(--font-size-sm);
  color: var(--color-text-placeholder);
}

.detail-meta i {
  margin-right: 4px;
}

/* 封面图 */
.detail-cover {
  margin-bottom: 40px;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.cover-img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--radius-md);
}

/* 正文 */
.detail-body {
  /* 建立 BFC：把后台富文本里的 float 图片收进正文框内。
     否则浮动图不计入父容器高度，会溢出到下方「上一篇/下一篇」并把它挤窄。 */
  display: flow-root;
  font-size: var(--font-size-base);
  color: var(--color-text-regular);
  line-height: var(--line-height-relaxed);
}

.detail-body :deep(p) {
  margin-bottom: 20px;
}

.detail-body :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 20px auto;
  border-radius: var(--radius-sm);
}

/* 同段多图并排：后台把多张图放进同一个 <p>（百分比宽度 + 行内布局），
   上面的 img{display:block} 会逐张拆成独立块、变成上下堆叠；
   此处用 flex 还原并排，间距交给后台的 width 控制。
   用 ~（一般兄弟）而非 +，以兼容图间夹 <br>/注释节点的情况。 */
.detail-body :deep(p:has(> img ~ img)) {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
}

.detail-body :deep(p:has(> img ~ img) > img) {
  margin: 20px 0;
}

.detail-body :deep(h1),
.detail-body :deep(h2),
.detail-body :deep(h3),
.detail-body :deep(h4) {
  color: var(--color-text-primary);
  margin: 30px 0 15px;
  font-weight: var(--font-weight-bold);
}

.detail-body :deep(h2) {
  font-size: 22px;
  padding-left: 12px;
  border-left: 4px solid var(--color-primary);
}

.detail-body :deep(h3) {
  font-size: 18px;
}

.detail-body :deep(ul),
.detail-body :deep(ol) {
  padding-left: 24px;
  margin-bottom: 20px;
}

.detail-body :deep(li) {
  margin-bottom: 8px;
}

.detail-body :deep(blockquote) {
  border-left: 4px solid var(--color-primary);
  background-color: var(--color-primary-bg);
  padding: 16px 20px;
  margin: 20px 0;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  color: var(--color-text-secondary);
}

.detail-body :deep(a) {
  color: var(--color-primary);
  text-decoration: none;
}

.detail-body :deep(a:hover) {
  text-decoration: underline;
}

.detail-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.detail-body :deep(th),
.detail-body :deep(td) {
  border: 1px solid var(--color-border);
  padding: 12px 16px;
  text-align: left;
}

.detail-body :deep(th) {
  background-color: var(--color-bg-lighter);
  font-weight: var(--font-weight-medium);
}

/* 上一篇/下一篇 */
.detail-nav {
  display: flex;
  gap: 20px;
  margin-top: 50px;
  padding-top: 30px;
  border-top: 1px solid var(--color-border-light);
}

.nav-item {
  flex: 1;
  padding: 20px;
  background-color: var(--color-bg-lighter);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  border: 1px solid transparent;
}

.nav-item:hover {
  background-color: var(--color-primary-bg);
  border-color: var(--color-primary-light);
}

.nav-item.prev {
  text-align: left;
}

.nav-item.next {
  text-align: right;
}

.nav-label {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--color-text-placeholder);
  margin-bottom: 8px;
}

.nav-item:hover .nav-label {
  color: var(--color-primary);
}

.nav-title {
  font-size: var(--font-size-base);
  color: var(--color-text-regular);
  font-weight: var(--font-weight-medium);
  max-width: 100%;
}

.nav-item:hover .nav-title {
  color: var(--color-primary);
}
/* 加载中 */
.loading-state {
  text-align: center;
  padding: 100px 0;
  color: var(--color-text-placeholder);
}

.loading-state i {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
  color: var(--color-primary);
}

.loading-state p {
  font-size: var(--font-size-md);
  margin: 0;
}

.empty-content {
  text-align: center;
  padding: 60px 0;
  color: var(--color-text-placeholder);
}

.empty-content i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.empty-content p {
  font-size: var(--font-size-base);
  margin: 0;
}
</style>
